import { useEffect, useRef, useState } from 'react'

import type { ToughCard } from '../../api/flashcards'
import { Button } from '../../ui/Control'
import { Icon } from '../../ui/Icon'
import { Preview } from '../../ui/preview/Preview'
import { roteLineMatches } from './roteLine'

export interface DrillResult {
  readonly repetitionsDone: number
  readonly mistakes: number
  readonly lastTyped: string
}

/** What one nut's drill has reached so far. `nut` says which nut it belongs to. */
interface Attempt {
  readonly nut: string
  readonly typed: string
  readonly streak: number
  readonly mistakes: number
  readonly shown: boolean
  readonly missed: boolean
}

const fresh = (nut: string): Attempt => ({
  nut,
  typed: '',
  streak: 0,
  mistakes: 0,
  shown: true,
  missed: false,
})

/**
 * Producing one mnemonic from memory, several times over.
 *
 * That the line is hidden from the second repetition on is the whole point and not a nicety of the
 * surface: copying something visible is the weak arm in the comparison studies. Nine of ten
 * repetitions have to be retrievals. For the same reason a wrong key is never quietly rejected —
 * otherwise one types one's way to the answer against the resistance of the field, and the exercise
 * is a transcription again.
 *
 * A miss says so loudly — the surface turns, the streak falls back to zero visibly, and the line is
 * put back in front of you. A difference of two words in a hint is not feedback anybody notices.
 *
 * The input element is deliberately the same one for every repetition: rebuilding it closes the
 * keyboard on Safari, and then nobody types ten times.
 */
export function Drill({
  card,
  line,
  repetitions,
  busy = false,
  onFinish,
}: {
  readonly card: ToughCard
  readonly line: string
  readonly repetitions: number
  readonly busy?: boolean
  readonly onFinish: (result: DrillResult, completed: boolean) => void
}) {
  const nut = `${card.cardId}-${card.direction}`
  const field = useRef<HTMLInputElement>(null)
  const [kept, setKept] = useState<Attempt>(() => fresh(nut))
  // Derived rather than reset in an effect: a new nut simply is a fresh attempt. The input element
  // itself survives that, which is the whole point — Safari would close the keyboard otherwise.
  const { typed, streak, mistakes, shown, missed } = kept.nut === nut ? kept : fresh(nut)

  useEffect(() => {
    field.current?.focus()
  }, [nut])

  const submit = () => {
    if (busy || typed.trim() === '') return
    if (roteLineMatches(line, typed)) {
      const done = streak + 1
      if (done >= repetitions) {
        onFinish({ repetitionsDone: done, mistakes, lastTyped: typed }, true)
        return
      }
      setKept({ nut, typed: '', streak: done, mistakes, shown: false, missed: false })
    } else {
      setKept({ nut, typed: '', streak: 0, mistakes: mistakes + 1, shown: true, missed: true })
    }
  }

  return (
    <section
      className={`drill${missed ? ' drill--missed' : ''}`}
      aria-label="Typing drill"
      aria-live="polite"
    >
      <div className="drill__card">
        <span className="drill__side-label">Question</span>
        <Preview text={card.question} format={card.questionFormat} label="Question" />
      </div>

      {missed && (
        <p className="drill__verdict">
          <Icon name="close" small />
          Not the line. The streak starts again — read it once more and type it from memory.
        </p>
      )}

      {shown ? (
        <div className="drill__line">
          <span className="drill__side-label">Say it out loud, then type it</span>
          <strong>{line}</strong>
        </div>
      ) : (
        <p className="drill__hidden">
          <Icon name="check" small />
          Hidden on purpose. Type it from memory and say it out loud.
        </p>
      )}

      <div className="drill__streak" key={streak}>
        <span className="drill__count">{`${streak} of ${repetitions}`}</span>
        <span className="drill__dots" aria-hidden="true">
          {Array.from({ length: repetitions }, (_, index) => (
            <span
              key={index}
              className={index < streak ? 'drill__dot drill__dot--done' : 'drill__dot'}
            />
          ))}
        </span>
      </div>

      <label className="card-field drill__field">
        <span className="card-field__label">Type the line</span>
        <input
          ref={field}
          className="card-field__control"
          value={typed}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoComplete="off"
          enterKeyHint="done"
          inputMode="text"
          onChange={(event) => {
            setKept({ nut, typed: event.target.value, streak, mistakes, shown, missed })
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              submit()
            }
          }}
        />
      </label>

      <footer className="drill__actions">
        <span className="drill__mistakes">
          {`${mistakes} ${mistakes === 1 ? 'mistake' : 'mistakes'}`}
        </span>
        <Button
          tone="bare"
          className="card-action--danger"
          disabled={busy}
          onClick={() => {
            onFinish({ repetitionsDone: streak, mistakes, lastTyped: typed }, false)
          }}
        >
          Stop drill
        </Button>
        <Button tone="primary" disabled={busy || typed.trim() === ''} onClick={submit}>
          Check <kbd>Enter</kbd>
        </Button>
      </footer>
    </section>
  )
}
